// src/features/properties/SearchFilters.jsx
import React, { useState, useEffect } from 'react';
import { getCities } from '../../services/api';
import { IoSearchOutline, IoFilterOutline, IoCloseOutline } from 'react-icons/io5';
import { FiBookOpen, FiUsers } from 'react-icons/fi';

const SearchFilters = ({ onFilterChange, activeFilters }) => {
    const [showFilters, setShowFilters] = useState(false);
    const [cities, setCities] = useState([]);
    const [localFilters, setLocalFilters] = useState({
        search: '',
        tipo: '',
        ciudad: '',
        precio_min: '',
        precio_max: '',
        habitaciones: '',
        para_estudiantes: false,
        ambiente_familiar: false,
        ...activeFilters,
    });

    useEffect(() => {
        getCities()
            .then(setCities)
            .catch(() => setCities([]));
    }, []);

    const handleSearchChange = (e) => {
        const newFilters = { ...localFilters, search: e.target.value };
        setLocalFilters(newFilters);
        onFilterChange(newFilters);
    };

    const handleFilterChange = (field, value) => {
        const newFilters = { ...localFilters, [field]: value };
        setLocalFilters(newFilters);
        onFilterChange(newFilters);
    };

    const clearFilters = () => {
        const empty = { search: '', tipo: '', ciudad: '', precio_min: '', precio_max: '', habitaciones: '', para_estudiantes: false, ambiente_familiar: false };
        setLocalFilters(empty);
        onFilterChange(empty);
    };

    const hasActiveFilters = localFilters.tipo || localFilters.ciudad || localFilters.precio_min || localFilters.precio_max || localFilters.habitaciones || localFilters.para_estudiantes || localFilters.ambiente_familiar;

    const inputClass = "rounded-xl border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-900 text-surface-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors cursor-pointer py-2.5 px-3 text-sm w-full";

    return (
        <div className="w-full mb-8">
            {/* Search bar */}
            <div className="flex gap-3">
                <div className="flex-1 relative">
                    <IoSearchOutline className="absolute left-4 top-1/2 -translate-y-1/2 text-surface-400 text-lg" />
                    <input
                        type="text"
                        placeholder="Buscar por ciudad, barrio, descripción..."
                        value={localFilters.search}
                        onChange={handleSearchChange}
                        className="input-field pl-11"
                    />
                </div>
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    aria-label="Mostrar filtros de búsqueda"
                    className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-200 cursor-pointer ${showFilters || hasActiveFilters
                        ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/25'
                        : 'bg-surface-100 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-500 dark:text-surface-400 hover:text-surface-700 dark:hover:text-white hover:border-surface-300 dark:hover:border-surface-600'
                        }`}
                >
                    <IoFilterOutline />
                    <span className="hidden sm:inline">Filtros</span>
                    {hasActiveFilters && (
                        <span className="bg-white text-brand-600 text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">!</span>
                    )}
                </button>
            </div>

            {/* Filter panel */}
            {showFilters && (
                <div className="mt-4 p-5 bg-white dark:bg-surface-850 rounded-2xl border border-surface-100 dark:border-surface-800 shadow-sm animate-slide-down">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-surface-900 dark:text-white font-semibold">Filtros avanzados</h3>
                        {hasActiveFilters && (
                            <button onClick={clearFilters} className="flex items-center gap-1 text-sm text-rose-500 hover:text-rose-600 transition-colors cursor-pointer">
                                <IoCloseOutline /> Limpiar
                            </button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        {/* Type */}
                        <div>
                            <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wider mb-1.5">Tipo</label>
                            <select
                                value={localFilters.tipo}
                                onChange={(e) => handleFilterChange('tipo', e.target.value)}
                                className={inputClass}
                            >
                                <option value="">Todos</option>
                                <optgroup label="Residencial">
                                    <option value="casa">Casa</option>
                                    <option value="apartamento">Apartamento</option>
                                    <option value="habitacion">Habitación</option>
                                    <option value="estudio">Estudio</option>
                                    <option value="finca">Finca</option>
                                </optgroup>
                                <optgroup label="Comercial">
                                    <option value="local">Local Comercial</option>
                                    <option value="oficina">Oficina</option>
                                    <option value="bodega">Bodega</option>
                                    <option value="consultorio">Consultorio</option>
                                    <option value="lote">Lote</option>
                                </optgroup>
                            </select>
                        </div>

                        {/* City */}
                        <div>
                            <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wider mb-1.5">Ciudad</label>
                            <select
                                value={localFilters.ciudad}
                                onChange={(e) => handleFilterChange('ciudad', e.target.value)}
                                className={inputClass}
                            >
                                <option value="">Todas</option>
                                {cities.map((city) => (
                                    <option key={city} value={city}>{city}</option>
                                ))}
                            </select>
                        </div>

                        {/* Min price */}
                        <div>
                            <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wider mb-1.5">Precio mín.</label>
                            <input
                                type="number"
                                placeholder="$0"
                                value={localFilters.precio_min}
                                onChange={(e) => handleFilterChange('precio_min', e.target.value)}
                                className={inputClass}
                            />
                        </div>

                        {/* Max price */}
                        <div>
                            <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wider mb-1.5">Precio máx.</label>
                            <input
                                type="number"
                                placeholder="$10,000,000"
                                value={localFilters.precio_max}
                                onChange={(e) => handleFilterChange('precio_max', e.target.value)}
                                className={inputClass}
                            />
                        </div>

                        {/* Rooms */}
                        <div>
                            <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wider mb-1.5">Habitaciones mín.</label>
                            <select
                                value={localFilters.habitaciones}
                                onChange={(e) => handleFilterChange('habitaciones', e.target.value)}
                                className={inputClass}
                            >
                                <option value="">Cualquiera</option>
                                <option value="1">1+</option>
                                <option value="2">2+</option>
                                <option value="3">3+</option>
                                <option value="4">4+</option>
                            </select>
                        </div>
                    </div>

                    {/* Checkboxes */}
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                        <label
                            htmlFor="para_estudiantes"
                            className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl border cursor-pointer transition-colors ${
                                localFilters.para_estudiantes
                                    ? 'border-brand-400 bg-brand-50 dark:bg-brand-900/20'
                                    : 'border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-900 hover:border-brand-300 hover:bg-brand-50 dark:hover:bg-brand-900/10'
                            }`}
                        >
                            <input
                                id="para_estudiantes"
                                type="checkbox"
                                checked={localFilters.para_estudiantes}
                                onChange={(e) => handleFilterChange('para_estudiantes', e.target.checked)}
                                className="w-4 h-4 text-brand-500 rounded border-surface-300 dark:border-surface-600 focus:ring-brand-500"
                            />
                            <FiBookOpen className="w-4 h-4 text-surface-500 dark:text-surface-400" />
                            <span className="text-sm text-surface-600 dark:text-surface-400">Solo para estudiantes</span>
                        </label>
                        <label
                            htmlFor="ambiente_familiar"
                            className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl border cursor-pointer transition-colors ${
                                localFilters.ambiente_familiar
                                    ? 'border-brand-400 bg-brand-50 dark:bg-brand-900/20'
                                    : 'border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-900 hover:border-brand-300 hover:bg-brand-50 dark:hover:bg-brand-900/10'
                            }`}
                        >
                            <input
                                id="ambiente_familiar"
                                type="checkbox"
                                checked={localFilters.ambiente_familiar}
                                onChange={(e) => handleFilterChange('ambiente_familiar', e.target.checked)}
                                className="w-4 h-4 text-brand-500 rounded border-surface-300 dark:border-surface-600 focus:ring-brand-500"
                            />
                            <FiUsers className="w-4 h-4 text-surface-500 dark:text-surface-400" />
                            <span className="text-sm text-surface-600 dark:text-surface-400">Ambiente familiar</span>
                        </label>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchFilters;
